import { ChevronDown, ChevronRight } from 'lucide-react';
import { FC, useCallback } from 'react';

import { ClusteringSectionProps } from './types';

/**
 * ClusteringSection - Cluster relationship configuration controls
 *
 * This section provides radio button controls for selecting the active clustering
 * relationship. Only one relationship can be active at a time (weight = 20).
 * Allows users to choose how entities are grouped in the graph visualization.
 *
 * Features:
 * - Radio button selection (single active weight)
 * - Visual feedback for selected relationship
 * - Collapsible section with accordion behavior
 * - Custom styled radio buttons with Chariot theme
 *
 * @param currentWeights - Array of available weight options with labels
 * @param onWeightChange - Callback to update weight values
 * @param isExpanded - Whether section is expanded
 * @param onToggle - Callback to toggle section expansion
 */
export const ClusteringSection: FC<ClusteringSectionProps> = ({
  currentWeights,
  onWeightChange,
  isExpanded,
  onToggle,
}) => {
  const handleClusterChange = useCallback(
    (selectedName: string) => {
      currentWeights.forEach(({ name }) => {
        onWeightChange(name, name === selectedName ? 20 : 0);
      });
    },
    [currentWeights, onWeightChange]
  );

  return (
    <div className="flex flex-col gap-2">
      <button
        onClick={onToggle}
        className="text-chariot-text-primary hover:text-chariot-text-primary-hover flex items-center gap-1 p-0 text-left text-sm font-semibold transition-colors"
      >
        {isExpanded ? (
          <ChevronDown className="size-4 shrink-0" />
        ) : (
          <ChevronRight className="size-4 shrink-0" />
        )}
        Set Cluster By Relationship
      </button>
      {isExpanded && (
        <div className="ml-5 flex flex-col gap-2">
          {currentWeights.map(({ label, name, weight }) => (
            <label
              key={name}
              className="group text-chariot-text-secondary hover:text-chariot-text-primary flex cursor-pointer items-center gap-3 text-sm transition-colors"
            >
              <div className="relative flex size-5 items-center justify-center">
                <input
                  type="radio"
                  name="cluster"
                  checked={weight === 20}
                  onChange={() => {
                    handleClusterChange(name);
                  }}
                  className="peer border-chariot-stroke checked:border-chariot-brand-primary checked:bg-chariot-brand-primary/10 absolute size-5 cursor-pointer appearance-none rounded-full border-2 bg-transparent transition-all"
                />
                <div className="bg-chariot-brand-primary pointer-events-none absolute size-2.5 scale-0 rounded-full transition-transform duration-150 peer-checked:scale-100" />
              </div>
              <span className="group-hover:text-chariot-text-primary text-sm transition-colors select-none">
                {label}
              </span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};
